import { Grid, GridItem } from '@chakra-ui/react';

import { COUNTRY_CONFIG } from '../../constants/countries';
import { Country } from '../../gql/generated/graphql';
import AutocompleteGoogleAddress from './AutocompleteGoogleAddress';
import Input from './Input';
import Select from './Select';

type AddressInputProps = {
  label?: string;
  prefix?: string;
  dropLabel?: boolean;
  autoCompleteDisabled?: boolean;
  country: Country;
};

const AddressInput = ({
  label,
  prefix,
  dropLabel,
  autoCompleteDisabled,
  country,
}: AddressInputProps) => {
  const { regions, regionLabel, postalCode, hasCounty } = COUNTRY_CONFIG[country];
  const formattedPrefix = prefix ? `${prefix}_` : '';

  const getAutoComplete = (type: string) => {
    if (autoCompleteDisabled) {
      return 'off';
    }
    return `${prefix}_${type}`;
  };

  return (
    <Grid
      templateColumns={{
        base: '1fr',
        md: '1fr 1fr 1fr',
      }}
      gridColumnGap="16px"
      gridRowGap="20px"
      alignItems={dropLabel ? 'start' : 'end'}
    >
      <GridItem
        colSpan={{
          base: 1,
          md: 2,
        }}
      >
        <AutocompleteGoogleAddress
          label={label}
          prefix={formattedPrefix}
          name={`${formattedPrefix}address_line`}
          placeholder={autoCompleteDisabled ? 'Search Street' : 'Street'}
          dropLabel={dropLabel}
          country={country}
        />
      </GridItem>
      <GridItem colSpan={1}>
        <Input
          name={`${formattedPrefix}address_line_2`}
          autoCompleteDisabled={autoCompleteDisabled}
          placeholder="Apt., Suite, Unit"
          _input={{
            autoComplete: getAutoComplete('address_line_2'),
          }}
        />
      </GridItem>
      <GridItem
        colSpan={{
          base: 1,
          md: 2,
        }}
      >
        <Input
          name={`${formattedPrefix}city`}
          autoCompleteDisabled={autoCompleteDisabled}
          placeholder="City"
          _input={{
            autoComplete: getAutoComplete('city'),
          }}
        />
      </GridItem>
      <GridItem colSpan={1}>
        <Select
          name={`${formattedPrefix}state`}
          options={regions}
          placeholder={`Search ${regionLabel}`}
          _input={{
            autoComplete: getAutoComplete('state'),
          }}
        />
      </GridItem>
      <GridItem
        colSpan={{
          base: 1,
          md: 2,
        }}
      >
        <Input
          name={`${formattedPrefix}zip`}
          autoCompleteDisabled={autoCompleteDisabled}
          placeholder={postalCode.label}
          mask={postalCode.mask}
          textTransform="uppercase"
          _input={{
            autoComplete: getAutoComplete('zip'),
            sx: { '&::placeholder': { textTransform: 'none' } },
          }}
        />
      </GridItem>
      {hasCounty && (
        <GridItem colSpan={1}>
          <Input
            name={`${formattedPrefix}county`}
            autoCompleteDisabled={autoCompleteDisabled}
            placeholder="County"
            _input={{
              autoComplete: getAutoComplete('county'),
            }}
          />
        </GridItem>
      )}
    </Grid>
  );
};

export default AddressInput;
